import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import { isLoggedIn } from './IsLoggedIn';
import { getAuthUser } from '../utils/auth';
import { isGrainMill } from '../utils/companyTypes';
import { Navigate } from 'react-router-dom';
import RoutePermissionGuard from 'components/permission/RoutePermissionGuard';
import PackingAdvancedRouteGuard from 'components/permission/PackingAdvancedRouteGuard';

// render- Dashboard
const DashboardDefault = Loadable(lazy(() => import('../pages/dashboard/default')));
const ManufacturerDashboard = Loadable(lazy(() => import('../pages/dashboard/manufacturer')));
const GrainMillDashboard = Loadable(lazy(() => import('../pages/dashboard/GrainMillDashboard')));
const RMLedger = Loadable(lazy(() => import('../pages/component-overview/FlourMill/RMLedger')));
const LotStock = Loadable(lazy(() => import('../pages/component-overview/FlourMill/LotStock')));
const LotTrace = Loadable(lazy(() => import('../pages/component-overview/FlourMill/LotTrace')));
const DailyAccounts = Loadable(lazy(() => import('../pages/component-overview/FlourMill/DailyAccounts')));
const UserPerformance = Loadable(lazy(() => import('../pages/component-overview/FlourMill/UserPerformance')));
const UserPerformanceDetail = Loadable(lazy(() => import('../pages/component-overview/FlourMill/UserPerformanceDetail')));
const GrainMillSalesmen = Loadable(lazy(() => import('../pages/component-overview/GrainMill/Salesmen')));
const GrainMillPartners = Loadable(lazy(() => import('../pages/component-overview/GrainMill/Partners')));
const GrainMillMachines = Loadable(lazy(() => import('../pages/component-overview/GrainMill/Machines')));
const GrainMillRawReport = Loadable(lazy(() => import('../pages/component-overview/GrainMill/GrainMillRawReport')));
const GrainMillShortageReport = Loadable(lazy(() => import('../pages/component-overview/GrainMill/GrainMillShortageReport')));
const GrainMillProcessingReport = Loadable(lazy(() => import('../pages/component-overview/GrainMill/GrainMillProcessingReport')));
const GrainMillDailyCashBook = Loadable(lazy(() => import('../pages/component-overview/GrainMill/DailyCashBook')));
const GrainMillContractors = Loadable(lazy(() => import('../pages/component-overview/GrainMill/Contractors')));
const GrainMillTransportReport = Loadable(lazy(() => import('../pages/component-overview/GrainMill/TransportReport')));
const TransportDrivers = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/Drivers')));
const TransportVehicles = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/Vehicles')));
const TransportRoutes = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/Routes')));
const TransportTrips = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/TripsSwitch')));
const TransportTripDetail = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/TripDetailSwitch')));
const TransportDriverBoard = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/DriverBoard')));
const TransportGatePasses = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/GatePasses')));
const TransportGatePassForm = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/GatePassForm')));
const TransportExpenses = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/Expenses')));
const TransportVehicleLedger = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/VehicleLedger')));
const TransportReports = Loadable(lazy(() => import('../pages/component-overview/FlourMill/Transport/Reports')));
const NotFound = Loadable(lazy(() => import('../pages/component-overview/NotFoundPage')));

const Customer = Loadable(lazy(() => import('../pages/component-overview/Clients/Customer/Customer')));
const Supplier = Loadable(lazy(() => import('../pages/component-overview/Clients/Supplier/Supplier')));
const Products = Loadable(lazy(() => import('../pages/component-overview/products/ProductList')));
const ProductCategories = Loadable(lazy(() => import('../pages/component-overview/products/CategoryList')));
const Stock = Loadable(lazy(() => import('../pages/component-overview/stock/StockOverview')));
const FinishedProductStock = Loadable(lazy(() => import('../pages/component-overview/stock/FinishedProductStockPage')));
const StockAdjustment = Loadable(lazy(() => import('../pages/component-overview/stock/StockAdjustment')));
const StockTransfer = Loadable(lazy(() => import('../pages/component-overview/stock/StockTransfer')));
const StockAlerts = Loadable(lazy(() => import('../pages/component-overview/stock/StockAlerts')));
const SalesOrderList = Loadable(lazy(() => import('../pages/component-overview/Sales/SalesOrderList')));
const PurchaseOrderList = Loadable(lazy(() => import('../pages/component-overview/Purchase/PurchaseOrderList')));
const CreateSalesOrder = Loadable(lazy(() => import('../pages/component-overview/Sales/CreateSalesOrder')));
const CreatePurchaseOrder = Loadable(lazy(() => import('../pages/component-overview/Purchase/CreatePurchaseOrder')));
const EditPurchaseOrder = Loadable(lazy(() => import('../pages/component-overview/Purchase/EditPurchaseOrder')));
const EditSalesOrder = Loadable(lazy(() => import('../pages/component-overview/Sales/EditSalesOrder')));
const AssignedOrders = Loadable(lazy(() => import('../pages/component-overview/Sales/AssignedOrders')));
// Order Fulfillment module (Phase 2A) — standalone, WhatsApp/PDF order tracking.
const NewFulfillmentOrder = Loadable(lazy(() => import('../pages/component-overview/FulfillmentOrders/NewFulfillmentOrder')));
const MyFulfillmentOrders = Loadable(lazy(() => import('../pages/component-overview/FulfillmentOrders/MyFulfillmentOrders')));
// Phase 2B — Order Detail (stepper) + admin dashboard. Distinct name from
// FulfillmentDetail below (an unrelated, pre-existing Sales Order page)
// on purpose, to avoid any import collision.
const FulfillmentOrderDetail = Loadable(lazy(() => import('../pages/component-overview/FulfillmentOrders/FulfillmentOrderDetail')));
const AllFulfillmentOrders = Loadable(lazy(() => import('../pages/component-overview/FulfillmentOrders/AllFulfillmentOrders')));
const FulfillmentDetail = Loadable(lazy(() => import('../pages/component-overview/Sales/FulfillmentDetail')));
const Users = Loadable(lazy(() => import('../pages/component-overview/Users/Users')));
const CompanyPermissions = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorPermissions/CompanyPermissions')));
const ManufacturerCompanyPermissions = Loadable(lazy(() => import('../pages/component-overview/Company/ManufacturerPermissions/ManufacturerCompanyPermissions')));
const ManufacturerRoleDefaults = Loadable(lazy(() => import('../pages/component-overview/Company/ManufacturerPermissions/RoleDefaultsPage')));
const ManufacturerUserPermissionsOverride = Loadable(lazy(() => import('../pages/component-overview/Company/ManufacturerPermissions/UserPermissionsOverride')));
const UserPermissions = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorPermissions/UserPermissions')));
const DistributorInvoiceList = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorInvoices/InvoiceList')));
const DistributorInvoiceForm = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorInvoices/InvoiceForm')));
const DistributorChequeList = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorCheques/ChequeList')));
const DistributorCreditNoteList = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorCreditNotes/CreditNoteList')));
const DistributorDebitNoteList = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorDebitNotes/DebitNoteList')));
const DistributorPriceListPage = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorPriceLists/PriceListPage')));
const DistributorCoaPage = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorCoa/CoaPage')));
const DistributorCostCenterPage = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorCostCenters/CostCenterPage')));
const DistributorDepartmentPage = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorDepartments/DepartmentPage')));
const DistributorDiscountRulePage = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorDiscountRules/DiscountRulePage')));
const DistributorDashboardHome = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorDashboard/DashboardHome')));
const DistributorSalesReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/SalesReport')));
const DistributorPurchaseReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/PurchaseReport')));
const DistributorAgingReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/AgingReport')));
const DistributorExpenseReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/ExpenseReport')));
const DistributorStockAdjustmentReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/StockAdjustmentReport')));
const DistributorTrialBalanceReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/TrialBalanceReport')));
const DistributorBalanceSheetReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/BalanceSheetReport')));
const DistributorIncomeStatementReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/IncomeStatementReport')));
const DistributorCashFlowReport = Loadable(lazy(() => import('../pages/component-overview/Company/DistributorReports/CashFlowReport')));
const ProductionProcessing = Loadable(lazy(() => import('../pages/component-overview/Wastage/ProductionProcessing')));
// ── Manufacturing tab (NEW standalone module — Suppliers/Raw Material/
// Ready Material/Accounts). Separate from the manufacturer/recipes+
// production BOM system above, per manufacturingModuleMigration.js.
const ManufacturingSuppliers = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/SuppliersPage')));
// Old standalone Raw Material CRUD page removed — this URL (kept as-is so
// the existing seeded sidebar item still works) now renders the same
// Products page as System A, locked to product_type='packing_material'.
const ManufacturingRawMaterial = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/PackingProductsPage')));
const ManufacturingPurchaseOrders = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/PackingPurchaseOrdersPage')));
const ManufacturingCreatePurchaseOrder = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/PackingCreatePurchaseOrderPage.jsx')));
const ManufacturingStock = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/PackingStockPage')));
const ManufacturingReadyMaterial = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/ReadyMaterialPage')));
const ManufacturingAccounts = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/AccountsPage')));
// ── Raw Material module (Phase 2) — same reuse pattern as Packing
// Material above, just no Ready Material page/route.
const RMSuppliers = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMSuppliersPage')));
const RMProducts = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMProductsPage')));
const RMPurchaseOrders = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMPurchaseOrdersPage')));
const RMCreatePurchaseOrder = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMCreatePurchaseOrderPage')));
const RMStock = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMStockPage')));
const RMAccounts = Loadable(lazy(() => import('../pages/component-overview/Manufacturing/RMAccountsPage')));
// ── Manufacturing 2 (company_type = 'manufacturing_2') — ek Purchase dropdown,
// har page par Raw / Packing toggle (components/manufacturing2/MaterialSwitcher).
// Ready Material: purana packing page seedha reuse (toggle nahi).
const M2Suppliers = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2SuppliersPage')));
const M2Products = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2ProductsPage')));
const M2PurchaseOrders = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2PurchaseOrdersPage')));
const M2CreatePurchaseOrder = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2CreatePurchaseOrderPage')));
const M2Stock = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2StockPage')));
const M2Accounts = Loadable(lazy(() => import('../pages/component-overview/Manufacturing2/M2AccountsPage')));
const Pettycashmanagement = Loadable(lazy(() => import('../pages/component-overview/PettyCash/Pettycashmanagement')));
// Sub Petty Cash (Manufacturer only) — see SubPettyCashManagement.jsx
const SubPettyCashManagement = Loadable(lazy(() => import('../pages/component-overview/SubPettyCash/SubPettyCashManagement')));
const Wastagereport = Loadable(lazy(() => import('../pages/component-overview/Wastage/WastageReports/Wastagereport')));
const BankManagement = Loadable(lazy(() => import('../pages/component-overview/Bank/BankAccountManagement')));
const EmployeeManagement = Loadable(lazy(() => import('../pages/component-overview/Employee/EmployeeManagement')));
const Reports = Loadable(lazy(() => import('../pages/component-overview/Reports/Reports')));
const SalaryManagement = Loadable(lazy(() => import('../pages/component-overview/Salary/SalaryManagement')));
const Expense = Loadable(lazy(() => import('../pages/component-overview/Clients/Expense/Expense')));
const GoodsReceivedNote = Loadable(lazy(() => import('../pages/component-overview/GRN/GoodsReceivedNote')));
const PurchaseWastageReport = Loadable(lazy(() => import('../pages/component-overview/Purchase/PurchaseWastageReport')));
const GRNPrintTemplate = Loadable(lazy(() => import('../pages/component-overview/GRN/GRNPrintTemplate')));
const ProductionOrderPrintTemplate = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Production/ProductionOrderPrintTemplate')));
const ProductionBatchPrintTemplate = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Production/ProductionBatchPrintTemplate')));
const AccountReceivables = Loadable(lazy(() => import('../pages/component-overview/Accounts/AccountReceivables')));
const AccountPayables = Loadable(lazy(() => import('../pages/component-overview/Accounts/AccountPayables')));
const ReceivablesPayables = Loadable(lazy(() => import('../pages/component-overview/Accounts/ReceivablesPayables')));
const EmployeeLoans = Loadable(lazy(() => import('../pages/component-overview/Employee/EmployeeLoans')));
const BankTransactionsHistory = Loadable(lazy(() => import('../pages/component-overview/Bank/BankTransactionsHistory')));
const Notifications = Loadable(lazy(() => import('../pages/component-overview/All_Notifications_Page/Notificaations')));
const CompaniesPage = Loadable(lazy(() => import('../pages/component-overview/Company/CompaniesPage')));
const RetailPOSPage = Loadable(lazy(() => import('../pages/component-overview/RetailPOS/RetailPOSPage')));
const SaleReturnList = Loadable(lazy(() => import('../pages/component-overview/Sales/SaleReturnList')));
const PurchaseReturnList = Loadable(lazy(() => import('../pages/component-overview/Purchase/PurchaseReturnList')));
const CreateSaleReturn = Loadable(lazy(() => import('../pages/component-overview/Sales/CreateSaleReturn')));
const CreatePurchaseReturn = Loadable(lazy(() => import('../pages/component-overview/Purchase/CreatePurchaseReturn')));
const SalesView = Loadable(lazy(() => import('../pages/component-overview/Sales/SalesView')));

// ── Manufacturer Module — Phase 3 (BOM Recipes + Production Orders) ──
const RecipeList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Recipes/RecipeList')));
const RecipeForm = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Recipes/RecipeForm')));
const ProductionOrderList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Production/ProductionOrderList')));
const PriceLevelList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/PriceLevels/PriceLevelList')));
const PriceMatrix = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/PriceLevels/PriceMatrix')));
const ManufacturerLowStock = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/PriceLevels/ManufacturerLowStock')));
// Manufacturer Module — Customer Classification (Retailer / Madaris / HORECA etc.)
const CustomerTypeList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/CustomerTypes/CustomerTypeList')));
// Manufacturer Module — Phase 6, Batch 1 (Delivery Challan)
const DeliveryChallanList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/DeliveryChallans/DeliveryChallanList')));
const CreateDeliveryChallan = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/DeliveryChallans/CreateDeliveryChallan')));
const DeliveryChallanDetail = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/DeliveryChallans/DeliveryChallanDetail')));
// Manufacturer Module — Phase 6, Batch 2 (Invoice)
const InvoiceList = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Invoices/InvoiceList')));
const TaxReportPage = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/TaxReport/TaxReportPage')));
const CreateInvoice = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Invoices/CreateInvoice')));
const InvoiceDetail = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Invoices/InvoiceDetail')));
const InvoicePrintTemplate = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Invoices/InvoicePrintTemplate')));
const InvoicePrintTemplateV2 = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Invoices/InvoicePrintTemplateV2')));
const CreateProductionEntry = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Production/CreateProductionEntry')));
const ProductionOrderDetail = Loadable(lazy(() => import('../pages/component-overview/Manufacturer/Production/ProductionOrderDetail')));
const PurchaseView = Loadable(lazy(() => import('../pages/component-overview/Purchase/PurchaseView')));
const CompanyProfile = Loadable(lazy(() => import('../pages/component-overview/Company/CompanyProfile')));
const Credit = Loadable(lazy(() => import('../pages/component-overview/Credit/Credit')));
const TableFloorSetup = Loadable(lazy(() => import('../pages/component-overview/Restaurant/TableFloorSetup')));
const MenuManagement = Loadable(lazy(() => import('../pages/component-overview/Restaurant/MenuManagement')));
const OrderTaking = Loadable(lazy(() => import('../pages/component-overview/Restaurant/OrderTaking')));
const KitchenDisplay = Loadable(lazy(() => import('../pages/component-overview/Restaurant/KitchenDisplay')));
const FloorView = Loadable(lazy(() => import('../pages/component-overview/Restaurant/FloorView')));
const RestaurantHome = Loadable(lazy(() => import('../pages/component-overview/Restaurant/RestaurantHome')));
const RestaurantOrders = Loadable(lazy(() => import('../pages/component-overview/Restaurant/RestaurantOrders')));
const ShiftManagement = Loadable(lazy(() => import('../pages/component-overview/Restaurant/ShiftManagement')));
const LicenseManagement = Loadable(lazy(() => import('../pages/component-overview/License/LicenseManagement')));

const MainRoutes = {
  path: '/',
  element: <DashboardLayout />,
  children: [
    {
      path: '/',
      element: isLoggedIn() ? <DashboardDefault /> : <Navigate to="/login" />
    },
    {
      path: 'dashboard',
      children: [
        {
          path: 'default',
          element: isLoggedIn() ? <DashboardDefault /> : <Navigate to="/login" />
        },
        {
          path: 'manufacturer',
          element: isLoggedIn() ? <ManufacturerDashboard /> : <Navigate to="/login" />
        },
        {
          path: 'grain-mill',
          element: isLoggedIn() ? <RoutePermissionGuard code="dashboard.view"><GrainMillDashboard /></RoutePermissionGuard> : <Navigate to="/login" />
        },
        {
          // Retailer now uses the same default (wholesaler) dashboard —
          // this route is kept only so old links/bookmarks still work.
          path: 'retailer',
          element: isLoggedIn() ? <DashboardDefault /> : <Navigate to="/login" />
        }
      ]
    },
    {
      path: 'customers',
      element: isLoggedIn() ? <RoutePermissionGuard code="clients.view"><Customer /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'suppliers',
      element: isLoggedIn() ? <RoutePermissionGuard code="clients.view"><Supplier /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'company',
      element: isLoggedIn() ? <CompaniesPage /> : <Navigate to="/login" />
    },
    {
      path: 'licenses',
      element: isLoggedIn() ? <LicenseManagement /> : <Navigate to="/login" />
    },
    {
      path: 'company-profile',
      element: isLoggedIn() ? <CompanyProfile /> : <Navigate to="/login" />
    },
    {
      path: 'products',
      element: isLoggedIn() ? <RoutePermissionGuard code="products.view"><Products /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'product-categories',
      element: isLoggedIn() ? <RoutePermissionGuard code="categories.view"><ProductCategories /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'stock',
      element: isLoggedIn() ? <RoutePermissionGuard code="stock.view"><Stock /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      // Grain Mill me ye page ab nahi (menu hata diya): direct URL par dashboard par bhej do.
      path: 'finished-products-stock',
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        <Navigate to="/dashboard/default" />
      ) : (
        <RoutePermissionGuard code="stock.view"><FinishedProductStock /></RoutePermissionGuard>
      )
    },
    {
      path: 'notifications',
      element: isLoggedIn() ? <Notifications /> : <Navigate to="/login" />
    },
    {
      path: 'pos',
      element: isLoggedIn() ? <RoutePermissionGuard code="pos.sale"><RetailPOSPage /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/home',
      element: isLoggedIn() ? <RestaurantHome /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/orders',
      element: isLoggedIn() ? <RestaurantOrders /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/tables',
      element: isLoggedIn() ? <TableFloorSetup /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/floor-view',
      element: isLoggedIn() ? <FloorView /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/menu',
      element: isLoggedIn() ? <MenuManagement /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/order-taking',
      element: isLoggedIn() ? <OrderTaking /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/kitchen',
      element: isLoggedIn() ? <KitchenDisplay /> : <Navigate to="/login" />
    },
    {
      path: 'restaurant/shift',
      element: isLoggedIn() ? <ShiftManagement /> : <Navigate to="/login" />
    },
    {
      path: 'stock-adjustments',
      element: isLoggedIn() ? <RoutePermissionGuard code="stock.adjust"><StockAdjustment /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'stock-transfers',
      element: isLoggedIn() ? <RoutePermissionGuard code="stock.transfer"><StockTransfer /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'stock-alerts',
      element: isLoggedIn() ? <StockAlerts /> : <Navigate to="/login" />
    },
    {
      path: 'sales',
      element: isLoggedIn() ? <RoutePermissionGuard code="sales.view"><SalesOrderList /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'sals-create',
      element: isLoggedIn() ? <RoutePermissionGuard code="sales.create"><CreateSalesOrder /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'sales-edit/:id',
      element: isLoggedIn() ? <RoutePermissionGuard code="sales.edit"><EditSalesOrder /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/assigned-orders',
      element: isLoggedIn() ? <AssignedOrders /> : <Navigate to="/login" />
    },
    {
      // Order Fulfillment module — Phase 2A. Standalone (not under manufacturer/),
      // visible to any company type once fulfillment_orders.* permissions are assigned.
      path: 'fulfillment-orders/new',
      element: isLoggedIn() ? <NewFulfillmentOrder /> : <Navigate to="/login" />
    },
    {
      path: 'fulfillment-orders/my',
      element: isLoggedIn() ? <MyFulfillmentOrders /> : <Navigate to="/login" />
    },
    {
      // Order Fulfillment module — Phase 2B admin dashboard (fulfillment_orders.view_all).
      path: 'fulfillment-orders/all',
      element: isLoggedIn() ? <AllFulfillmentOrders /> : <Navigate to="/login" />
    },
    {
      // Order Fulfillment module — Phase 2B detail/stepper page. Registered
      // AFTER /new, /my, /all so those literal segments are matched first.
      path: 'fulfillment-orders/:id',
      element: isLoggedIn() ? <FulfillmentOrderDetail /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/assigned-orders/:id',
      element: isLoggedIn() ? <FulfillmentDetail /> : <Navigate to="/login" />
    },
    {
      path: 'sales-view/:id',
      element: isLoggedIn() ? <RoutePermissionGuard code="sales.view"><SalesView /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'purchases',
      element: isLoggedIn() ? <RoutePermissionGuard code="purchases.view"><PurchaseOrderList /></RoutePermissionGuard> : <Navigate to="/login" />
    },

    {
      path: 'pur-create',
      element: isLoggedIn() ? <RoutePermissionGuard code="purchases.create"><CreatePurchaseOrder /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'purchase-edit/:id',
      element: isLoggedIn() ? <RoutePermissionGuard code="purchases.edit"><EditPurchaseOrder /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'purchase-view/:id',
      element: isLoggedIn() ? <RoutePermissionGuard code="purchases.view"><PurchaseView /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'users',
      element: isLoggedIn() ? <Users /> : <Navigate to="/login" />
    },
    // ── Distributor permission system (distributor type only, gated inside each page) ──
    {
      path: 'admin/companies/:id/permissions',
      element: isLoggedIn() ? <CompanyPermissions /> : <Navigate to="/login" />
    },
    {
      path: 'company/users/:id/permissions',
      element: isLoggedIn() ? <UserPermissions /> : <Navigate to="/login" />
    },
    // ── Manufacturer permission system (manufacturer type only, F1 — company ceiling) ──
    {
      path: 'admin/manufacturer-companies/:id/permissions',
      element: isLoggedIn() ? <ManufacturerCompanyPermissions /> : <Navigate to="/login" />
    },
    {
      // Super Admin, explicit company (from CompaniesPage)
      path: 'admin/manufacturer-companies/:id/roles',
      element: isLoggedIn() ? <ManufacturerRoleDefaults /> : <Navigate to="/login" />
    },
    {
      // Company admin, own company (no :id — resolved from logged-in user)
      path: 'manufacturer/roles',
      element: isLoggedIn() ? <ManufacturerRoleDefaults /> : <Navigate to="/login" />
    },
    {
      path: 'admin/manufacturer-users/:id/permissions',
      element: isLoggedIn() ? <ManufacturerUserPermissionsOverride /> : <Navigate to="/login" />
    },
    // ── Distributor Day 1 gap + Day 2 modules (Master Sync Prompt v2 §4 route map) ──
    {
      path: 'distributor/invoices',
      element: isLoggedIn() ? <DistributorInvoiceList /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/invoices/new',
      element: isLoggedIn() ? <DistributorInvoiceForm /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/invoices/:id',
      element: isLoggedIn() ? <DistributorInvoiceForm /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/cheques',
      element: isLoggedIn() ? <DistributorChequeList /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/credit-notes',
      element: isLoggedIn() ? <DistributorCreditNoteList /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/debit-notes',
      element: isLoggedIn() ? <DistributorDebitNoteList /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/price-lists',
      element: isLoggedIn() ? <DistributorPriceListPage /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/coa',
      element: isLoggedIn() ? <DistributorCoaPage /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/cost-centers',
      element: isLoggedIn() ? <DistributorCostCenterPage /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/departments',
      element: isLoggedIn() ? <DistributorDepartmentPage /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/discount-rules',
      element: isLoggedIn() ? <DistributorDiscountRulePage /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/dashboard',
      element: isLoggedIn() ? <DistributorDashboardHome /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/sales',
      element: isLoggedIn() ? <DistributorSalesReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/purchases',
      element: isLoggedIn() ? <DistributorPurchaseReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/aging',
      element: isLoggedIn() ? <DistributorAgingReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/expenses',
      element: isLoggedIn() ? <DistributorExpenseReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/stock-adjustments',
      element: isLoggedIn() ? <DistributorStockAdjustmentReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/trial-balance',
      element: isLoggedIn() ? <DistributorTrialBalanceReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/balance-sheet',
      element: isLoggedIn() ? <DistributorBalanceSheetReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/income-statement',
      element: isLoggedIn() ? <DistributorIncomeStatementReport /> : <Navigate to="/login" />
    },
    {
      path: 'distributor/reports/cash-flow',
      element: isLoggedIn() ? <DistributorCashFlowReport /> : <Navigate to="/login" />
    },
    {
      path: 'bank-transaction-history',
      element: isLoggedIn() ? <BankTransactionsHistory /> : <Navigate to="/login" />
    },
    {
      path: 'prod-procs',
      element: isLoggedIn() ? <ProductionProcessing /> : <Navigate to="/login" />
    },
    {
      path: 'petty-cash',
      element: isLoggedIn() ? <RoutePermissionGuard code="finance.transactions.view"><Pettycashmanagement /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      // Sub Petty Cash (Manufacturer only). Route itself is open to any
      // logged-in user like the other finance routes above — actual access
      // is gated by the sidebar (menu_items.company_type + permission) and
      // by the backend (checkManufacturerPermission + manufacturer-only
      // check in SubPettyCashController), same pattern as petty-cash.
      // Grain Mill me ye page nahi he: direct URL par dashboard par bhej do.
      path: 'sub-petty-cash',
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        <Navigate to="/dashboard/default" />
      ) : (
        <SubPettyCashManagement />
      )
    },
    {
      path: 'wastage',
      // Grain Mill me Wastage page nahi: direct URL par dashboard par bhej do.
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        <Navigate to="/dashboard/default" />
      ) : (
        <Wastagereport />
      )
    },
    {
      path: 'bank-accounts',
      element: isLoggedIn() ? <BankManagement /> : <Navigate to="/login" />
    },
    {
      path: 'employees',
      element: isLoggedIn() ? <RoutePermissionGuard code="employees.view"><EmployeeManagement /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'reports',
      element: isLoggedIn() ? <Reports /> : <Navigate to="/login" />
    },
    {
      path: 'salary',
      element: isLoggedIn() ? <SalaryManagement /> : <Navigate to="/login" />
    },
    {
      path: 'expenses',
      element: isLoggedIn() ? <RoutePermissionGuard code="expenses.view"><Expense /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'purchase-orders/:purchaseOrderId/grn',
      element: isLoggedIn() ? <GoodsReceivedNote /> : <Navigate to="/login" />
    },
    {
      // Manufacturer-only (page itself redirects non-manufacturer users away)
      path: 'purchases/wastage-report',
      element: isLoggedIn() ? <PurchaseWastageReport /> : <Navigate to="/login" />
    },
    // ── Manufacturer Module — Phase 3 (BOM Recipes + Production Orders) ──
    // All manufacturer-only; each page itself redirects non-manufacturer users away.
    {
      path: 'manufacturer/recipes',
      element: isLoggedIn() ? <RecipeList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/recipes/new',
      element: isLoggedIn() ? <RecipeForm /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/recipes/:id/edit',
      element: isLoggedIn() ? <RecipeForm /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/recipes/:id',
      element: isLoggedIn() ? <RecipeForm /> : <Navigate to="/login" />
    },
       {
      path: 'flour-mill/rm-ledger',
      element: isLoggedIn() ? <RMLedger /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/daily-accounts',
      // Grain Mill me Daily Accounts nahi (Salesman Report me wahi hisaab he): direct URL par dashboard par bhej do.
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        <Navigate to="/dashboard/default" />
      ) : (
        <DailyAccounts />
      )
    },
    {
      path: 'flour-mill/user-performance',
      element: isLoggedIn() ? <UserPerformance /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/user-performance/:userId',
      element: isLoggedIn() ? <UserPerformanceDetail /> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/partners',
      element: isLoggedIn() ? <RoutePermissionGuard code="partners.manage"><GrainMillPartners /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/machines',
      element: isLoggedIn() ? <RoutePermissionGuard code="machines.manage"><GrainMillMachines /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/salesmen',
      element: isLoggedIn() ? <RoutePermissionGuard anyOf={['salesmen.view', 'salesman_report.view']}><GrainMillSalesmen /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      // Salesman Report ab Salesmen page ka "Report" tab he; purane link / bookmark yahin redirect hote hain
      path: 'grain-mill/salesman-report',
      element: isLoggedIn() ? <Navigate to="/grain-mill/salesmen?tab=report" replace /> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/raw-report',
      element: isLoggedIn() ? <RoutePermissionGuard code="grain_mill_raw_report.view"><GrainMillRawReport /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/shortage-report',
      element: isLoggedIn() ? <RoutePermissionGuard code="grain_mill_raw_report.view"><GrainMillShortageReport /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/processing-report',
      element: isLoggedIn() ? <RoutePermissionGuard code="grain_mill_raw_report.view"><GrainMillProcessingReport /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/roznamcha',
      element: isLoggedIn() ? <RoutePermissionGuard code="finance.petty_cash.view"><GrainMillDailyCashBook /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/contractors',
      element: isLoggedIn() ? <RoutePermissionGuard code="gm_contractors.view"><GrainMillContractors /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'grain-mill/transport-report',
      element: isLoggedIn() ? <RoutePermissionGuard code="gm_transport_ledger.view"><GrainMillTransportReport /></RoutePermissionGuard> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/lot-stock',
      element: isLoggedIn() ? <LotStock /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/lot-trace',
      element: isLoggedIn() ? <LotTrace /> : <Navigate to="/login" />
    },
    // ── Flour Mill Transport (Step 1: masters). Pages khud flour_mill check karte hain ──
    {
      path: 'flour-mill/transport/drivers',
      element: isLoggedIn() ? <TransportDrivers /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/vehicles',
      element: isLoggedIn() ? <TransportVehicles /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/routes',
      element: isLoggedIn() ? <TransportRoutes /> : <Navigate to="/login" />
    },
    // ── Flour Mill Transport (Step 2: Trips + Gate Passes) ──
    {
      path: 'flour-mill/transport/trips',
      element: isLoggedIn() ? <TransportTrips /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/trips/:id',
      element: isLoggedIn() ? <TransportTripDetail /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/driver-board',
      element: isLoggedIn() ? <TransportDriverBoard /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/gate-passes',
      element: isLoggedIn() ? <TransportGatePasses /> : <Navigate to="/login" />
    },
       {
      path: 'flour-mill/transport/gate-passes/:id',
      element: isLoggedIn() ? <TransportGatePassForm /> : <Navigate to="/login" />
    },
    // ── Flour Mill Transport (Step 3: Expenses + Ledger + Reports) ──
    {
      path: 'flour-mill/transport/expenses',
      element: isLoggedIn() ? <TransportExpenses /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/vehicle-ledger',
      element: isLoggedIn() ? <TransportVehicleLedger /> : <Navigate to="/login" />
    },
    {
      path: 'flour-mill/transport/reports',
      element: isLoggedIn() ? <TransportReports /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/production',
      element: isLoggedIn() ? <ProductionOrderList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/production/new',
      element: isLoggedIn() ? <CreateProductionEntry /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/production/:id',
      element: isLoggedIn() ? <ProductionOrderDetail /> : <Navigate to="/login" />
    },
    // ── Manufacturer Module — Phase 5 (Trade Price Levels + Low-Stock) ──
    // All manufacturer-only; each page itself redirects non-manufacturer users away.
    {
      path: 'manufacturer/price-levels',
      element: isLoggedIn() ? <PriceLevelList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/price-levels/matrix',
      element: isLoggedIn() ? <PriceMatrix /> : <Navigate to="/login" />
    },
    // ── Manufacturer Module — Customer Classification ──
    {
      path: 'manufacturer/customer-types',
      element: isLoggedIn() ? <CustomerTypeList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/low-stock',
      element: isLoggedIn() ? <ManufacturerLowStock /> : <Navigate to="/login" />
    },
    // ── Manufacturer Module — Phase 6, Batch 1 (Delivery Challan) ──
    // All manufacturer-only; each page itself redirects non-manufacturer users away.
    // CreateDeliveryChallan is reached via ?order_id=<sales_order_id>
    // from Sales Order List's "Create Delivery Challan" action.
    {
      path: 'manufacturer/delivery-challans',
      element: isLoggedIn() ? <DeliveryChallanList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/delivery-challans/create',
      element: isLoggedIn() ? <CreateDeliveryChallan /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/delivery-challans/:id',
      element: isLoggedIn() ? <DeliveryChallanDetail /> : <Navigate to="/login" />
    },
    // ── Manufacturer Module — Phase 6, Batch 2 (Invoice) ──
    // All manufacturer-only; each page itself redirects non-manufacturer users away.
    // CreateInvoice is reached via ?order_id=<sales_order_id> from Delivery
    // Challan List/Detail's "Create Invoice" action.
    {
      path: 'manufacturer/invoices',
      element: isLoggedIn() ? <InvoiceList /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/tax-report',
      element: isLoggedIn() ? <TaxReportPage /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/invoices/create',
      element: isLoggedIn() ? <CreateInvoice /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/invoices/:id',
      element: isLoggedIn() ? <InvoiceDetail /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/invoices/:id/print',
      element: isLoggedIn() ? <InvoicePrintTemplate /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturer/invoices/:id/print-v2',
      element: isLoggedIn() ? <InvoicePrintTemplateV2 /> : <Navigate to="/login" />
    },
    // ── Manufacturing tab (NEW standalone module) ──
    // All manufacturer-only; each page itself redirects non-manufacturer
    // users away. URL segment is 'manufacturing' (not 'manufacturer') to
    // match the sidebar menu items seeded by manufacturingModuleMigration.js.
    {
      path: 'manufacturing/suppliers',
      element: isLoggedIn() ? <ManufacturingSuppliers /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/raw-material',
      element: isLoggedIn() ? <ManufacturingRawMaterial /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/purchase-orders',
      element: isLoggedIn() ? <ManufacturingPurchaseOrders /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/purchase-orders/create',
      element: isLoggedIn() ? <ManufacturingCreatePurchaseOrder /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/stock',
      element: isLoggedIn() ? <ManufacturingStock /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/ready-material',
      element: isLoggedIn() ? <PackingAdvancedRouteGuard><ManufacturingReadyMaterial /></PackingAdvancedRouteGuard> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing/accounts',
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        // Grain Mill me Accounts page nahi: direct URL par dashboard par bhej do.
        <Navigate to="/dashboard/default" />
      ) : (
        <ManufacturingAccounts />
      )
    },
    // ── Raw Material module (Phase 2) ──
    // Own URL prefix (not 'manufacturing/*', which Packing Material
    // already owns) — 'raw-material', matching the sidebar menu items
    // seeded by rawMaterialModuleMigration.js. No Ready Material route
    // here — this module doesn't have that step.
    {
      path: 'raw-material/suppliers',
      element: isLoggedIn() ? <RMSuppliers /> : <Navigate to="/login" />
    },
    {
      path: 'raw-material/products',
      element: isLoggedIn() ? <RMProducts /> : <Navigate to="/login" />
    },
    {
      path: 'raw-material/purchase-orders',
      element: isLoggedIn() ? <RMPurchaseOrders /> : <Navigate to="/login" />
    },
    {
      path: 'raw-material/purchase-orders/create',
      element: isLoggedIn() ? <RMCreatePurchaseOrder /> : <Navigate to="/login" />
    },
    {
      path: 'raw-material/stock',
      element: isLoggedIn() ? <RMStock /> : <Navigate to="/login" />
    },
    {
      path: 'raw-material/accounts',
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        // Grain Mill me Accounts page nahi: direct URL par dashboard par bhej do.
        <Navigate to="/dashboard/default" />
      ) : (
        <RMAccounts />
      )
    },
    // ── Manufacturing 2 — sidebar items seeded by manufacturing2Migration.js ──
    {
      path: 'manufacturing2/suppliers',
      element: isLoggedIn() ? <M2Suppliers /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/products',
      element: isLoggedIn() ? <M2Products /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/purchase-orders',
      element: isLoggedIn() ? <M2PurchaseOrders /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/purchase-orders/create',
      element: isLoggedIn() ? <M2CreatePurchaseOrder /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/stock',
      element: isLoggedIn() ? <M2Stock /> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/ready-material',
      element: isLoggedIn() ? <PackingAdvancedRouteGuard><ManufacturingReadyMaterial /></PackingAdvancedRouteGuard> : <Navigate to="/login" />
    },
    {
      path: 'manufacturing2/accounts',
      element: !isLoggedIn() ? (
        <Navigate to="/login" />
      ) : isGrainMill(getAuthUser().company_type) ? (
        // Grain Mill me Accounts page nahi: direct URL par dashboard par bhej do.
        <Navigate to="/dashboard/default" />
      ) : (
        <M2Accounts />
      )
    },

    {
      path: 'print/grn/:grnId',
      element: isLoggedIn() ? <GRNPrintTemplate /> : <Navigate to="/login" />
    },
    {
      path: 'print/production-order/:id',
      element: isLoggedIn() ? <ProductionOrderPrintTemplate /> : <Navigate to="/login" />
    },
    {
      path: 'print/production-batch/:id',
      element: isLoggedIn() ? <ProductionBatchPrintTemplate /> : <Navigate to="/login" />
    },
    {
      path: 'account-receivables',
      element: isLoggedIn() ? <AccountReceivables /> : <Navigate to="/login" />
    },
    {
      // Grain mill: Receivables | Payables | Trial Balance Report tabs
      path: 'receivables-payables',
      element: isLoggedIn() ? <ReceivablesPayables /> : <Navigate to="/login" />
    },
    {
      path: 'account-payables',
      element: isLoggedIn() ? <AccountPayables /> : <Navigate to="/login" />
    },
    {
      path: 'employee-loans',
      element: isLoggedIn() ? <EmployeeLoans /> : <Navigate to="/login" />
    },
    {
      path: 'sale-returns',
      element: isLoggedIn() ? <SaleReturnList /> : <Navigate to="/login" />
    },
    {
      path: 'sale-returns-create',
      element: isLoggedIn() ? <CreateSaleReturn /> : <Navigate to="/login" />
    },
    {
      path: 'purchase-returns',
      element: isLoggedIn() ? <PurchaseReturnList /> : <Navigate to="/login" />
    },
    {
      path: 'purchase-returns-create',
      element: isLoggedIn() ? <CreatePurchaseReturn /> : <Navigate to="/login" />
    },
    {
      path: '*',
      element: isLoggedIn() ? <NotFound /> : <Navigate to="/login" />
    },
    {
      path: 'credit',
      element: isLoggedIn() ? <Credit /> : <Navigate to="/login" />
    },

  ]
};

export default MainRoutes;